import React from "react";
import { Button } from "../../atoms/button/button";
import { Link, type LinkAction } from "../../atoms/link/link";
import { Text, TEXT_LEVELS, plainText, safeElement, type TextLevel } from "../../atoms/text/text";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../../atoms/close-glyph";
import { PriceBlock } from "../../molecules/price-block/price-block";
import { LINK_POSITION_BODY, fireLinkClick } from "../../services/analytics";
import { isSafeHref } from "../../services/safe-href";
import { isSafeImageSrc } from "../../services/safe-image-src";
import { slug } from "../../services/slug";
import { clickedHref } from "./dynamic-recommender-analytics";
import styles from "./recommender-card.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * One package in the Dynamic recommender's results: "Streaming Card"
 * 15286:28194 and "GP Card" 15286:28201, ECLIPSE Organisms. Built from the
 * engine's answer by dynamic-recommender-cards.ts, never authored directly.
 */
export const RECOMMENDER_CARD_VARIANTS = ["streaming", "genre-pack"] as const;
export type RecommenderCardVariant = (typeof RECOMMENDER_CARD_VARIANTS)[number];

/** The genre-pack card's corner glow. `blue` is a mini pack beside its base. */
export type RecommenderCardGlow = "amethyst" | "blue";

/** One logo tile — a streaming app plate, or a bare channel mark. */
export interface RecommenderCardLogo {
  logo_url: string;
  logo_alt: string;
}

export interface RecommenderCardProps {
  /** Defaults to `streaming`. */
  card_variant?: RecommenderCardVariant;
  /** The `genre-pack` card's glow. Defaults to `amethyst`; ignored by `streaming`. */
  glow_variant?: RecommenderCardGlow;
  /** A hex color for the glow, over either variant's own. Anything else is ignored. */
  glow_color?: string;
  name_label: string;
  /** Line under the name, e.g. "Genre pack". Omit for none. */
  type_label?: string;
  heading_level?: TextLevel;
  /** Label above the app tiles. Drawn only when there are tiles. */
  includes_label?: string;
  app_logos?: RecommenderCardLogo[];
  channel_logos?: RecommenderCardLogo[];
  /** Link after the channel marks, opened in the on-page modal. Needs both fields to render. */
  more_label?: string;
  more_href?: string;
  strikethrough_price?: string;
  price_dollars: string;
  /** Cents and frequency as one run, e.g. ".99/mo.". */
  price_cents?: string;
  /** Small print under the price. Absent, the legal run is omitted. */
  legal_content?: React.ReactNode;
  legal_link_label?: string;
  legal_link_href?: string;
  /** What the legal link does. Absent, it navigates to its href. */
  legal_link_action?: LinkAction;
  cta_label?: string;
  cta_href?: string;
  /** Where the call to action's and the channel link's clicks report. Defaults to "body". */
  link_position?: string;
  /** What the call to action's click reports, with its href resolved against the page. Defaults to its label and href. */
  cta_link_name?: string;
  /** What the channel link's click reports. Defaults to its label. */
  more_link_name?: string;
  /** Where the channel link's click reports it leads. Defaults to its href. */
  more_link_destination?: string;
  /** DOM id, and the front of the inner ids. */
  id?: string;
}

const LOGO_PX = 44;

/* Iframe Modal's TRIGGER_ATTRIBUTE and NAME_ATTRIBUTE, spelled out: that module
   is "use client", so importing a value from it here yields a client reference,
   not the string. The spec pins them together. The name is the page view's. */
const OPENS_IFRAME_MODAL = { "data-iframe-modal": "", "data-iframe-modal-name": "channel lineup" };

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

const VARIANT_SET: Record<RecommenderCardVariant, true> = {
  streaming: true,
  "genre-pack": true
};

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

/** A tile needs both an image and a name, or it is a gap a reader cannot read. */
function renderableLogos(value: unknown): RecommenderCardLogo[] {
  return list<RecommenderCardLogo>(value)
    .filter(logo => !!logo && isSafeImageSrc(text(logo.logo_url)) && text(logo.logo_alt) !== "")
    .map(logo => ({
      logo_url: text(logo.logo_url),
      logo_alt: text(logo.logo_alt)
    }));
}

function RecommenderCardImpl({
  card_variant: cardVariant,
  glow_variant: glowVariant,
  glow_color: glowColor,
  name_label: name,
  type_label: typeLabel,
  heading_level: headingLevel,
  includes_label: includesLabel,
  app_logos: appLogos,
  channel_logos: channelLogos,
  more_label: moreLabel,
  more_href: moreHref,
  strikethrough_price: strikethroughPrice,
  price_dollars: priceDollars,
  price_cents: priceCents,
  legal_content: legal,
  legal_link_label: legalLinkLabel,
  legal_link_href: legalLinkHref,
  legal_link_action: legalLinkAction,
  cta_label: ctaLabel,
  cta_href: ctaHref,
  link_position: linkPosition,
  cta_link_name: ctaLinkName,
  more_link_name: moreLinkName,
  more_link_destination: moreLinkDestination,
  id
}: RecommenderCardProps) {
  const packageName = text(name);
  const packageLabel = plainText(packageName);

  if (packageName === "" || text(priceDollars) === "") {
    return null;
  }

  const variant: RecommenderCardVariant = typeof cardVariant === "string" && Object.prototype.hasOwnProperty.call(VARIANT_SET, cardVariant) ?
    cardVariant :
    "streaming";
  const level = safeElement(headingLevel, "h3", TEXT_LEVELS);
  const anchor = slug(text(id));
  const prefix = anchor || `recommender-card-${slug(packageLabel) || "package"}`;
  const nameId = `${prefix}-name`;

  const apps = renderableLogos(appLogos);
  const channels = renderableLogos(channelLogos);
  const kind = text(typeLabel);
  const includes = text(includesLabel);

  const moreSafe = isSafeHref(moreHref) ? text(moreHref) : "";
  const legalLinkSafe = isSafeHref(legalLinkHref) ? text(legalLinkHref) : "";
  const moreLink = text(moreLabel) !== "" && moreSafe !== "";
  const legalCopy = typeof legal === "string" || legal == null ? text(legal) : legal;
  const hasLegal = typeof legalCopy === "string" ? legalCopy !== "" : legalCopy !== false;
  const legalLink = text(legalLinkLabel) !== "" && legalLinkSafe !== "";
  const cta = text(ctaLabel);
  /* `Button` puts `href` on an anchor with no scheme gate, unlike `Link`. */
  const ctaSafe = isSafeHref(ctaHref) ? text(ctaHref) : "";
  const position = text(linkPosition) || LINK_POSITION_BODY;
  /* The channel link reports its own click: the container would name it from the link's text, hidden package name included. */
  const reportMoreClick = (event: React.MouseEvent) => {
    if ((event.target as Element).closest("a")) {
      fireLinkClick({
        linkName: text(moreLinkName) || text(moreLabel),
        linkPosition: position,
        linkDestination: text(moreLinkDestination) || moreSafe
      });
    }
  };
  /* A named call to action reports its own click, with the href the browser resolved for it. */
  const reportCtaClick = text(ctaLinkName) ? (event: React.MouseEvent) => fireLinkClick({
    linkName: text(ctaLinkName),
    linkPosition: position,
    linkDestination: clickedHref(event)
  }) : undefined;
  /* Inline because the color is authored per product; the stylesheet only reads the property. */
  const glow = HEX_COLOR.test(text(glowColor)) ? { "--recommender-card-glow": text(glowColor) } as React.CSSProperties : undefined;

  return (
    <article
      id={anchor || undefined}
      {...(packageLabel ? { "aria-label": packageLabel } : { "aria-labelledby": nameId })}
      className={styles.card}
      data-glow={variant === "genre-pack" && glowVariant === "blue" ? "blue" : undefined}
      data-variant={variant}
      style={glow}
    >
      <div className={styles.content}>
        <div className={styles.stack}>
          <div className={styles.header}>
            <Text as={level} className={styles.name} id={nameId} variant="subhead-1" weight="medium">
              {packageName}
            </Text>
            {kind !== "" && (
              <Text as="p" className={styles.typeLabel} variant="eyebrow" weight="semibold">
                {kind}
              </Text>
            )}
          </div>

          {(apps.length > 0 || channels.length > 0 || moreLink) && (
            <div className={styles.logoArea}>
              {apps.length > 0 && includes !== "" && (
                <Text as="p" className={styles.includesLabel} variant="body-3" weight="medium">
                  {includes}
                </Text>
              )}
              {apps.length > 0 && (
                <ul className={styles.appRow}>
                  {apps.map((logo, index) => (
                    <li className={styles.appTile} key={`${index}-${logo.logo_url}`}>
                      <img
                        alt={logo.logo_alt}
                        className={styles.appLogo}
                        height={LOGO_PX}
                        loading="lazy"
                        src={logo.logo_url}
                        width={LOGO_PX}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {(channels.length > 0 || moreLink) && (
                <div className={styles.channelRow} onClick={moreLink ? reportMoreClick : undefined}>
                  {channels.length > 0 && (
                    <ul className={styles.channelList}>
                      {channels.map((logo, index) => (
                        <li key={`${index}-${logo.logo_url}`}>
                          <img
                            alt={logo.logo_alt}
                            className={styles.channelLogo}
                            height={LOGO_PX}
                            loading="lazy"
                            src={logo.logo_url}
                            width={LOGO_PX}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                  {moreLink && (
                    <Link
                      contrast="high"
                      dataAttributes={OPENS_IFRAME_MODAL}
                      href={moreSafe}
                      size="small"
                    >
                      {text(moreLabel)}
                      <span className="dds-sr-only"> {packageLabel}</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          <PriceBlock
            cents={text(priceCents) || undefined}
            className={styles.price}
            dollars={text(priceDollars)}
            legal={!hasLegal && !legalLink ? undefined : (
              <>
                {legalCopy}
                {legalLink && (
                  <>
                    {hasLegal && " "}
                    <Link
                      contrast="high"
                      href={legalLinkSafe}
                      linkAction={legalLinkAction}
                      linkName={text(legalLinkLabel)}
                      linkPosition={LINK_POSITION_BODY}
                      size="small"
                    >
                      {text(legalLinkLabel)}
                    </Link>
                  </>
                )}
              </>
            )}
            strikethroughPrice={text(strikethroughPrice) || undefined}
          />
        </div>

        {cta !== "" && (
          <Button
            fullWidth
            href={ctaSafe || undefined}
            {...(reportCtaClick ? { onClick: reportCtaClick } : {
              linkName: cta,
              linkPosition: position
            })}
            size="medium"
          >
            {cta}
            <span className="dds-sr-only"> {packageLabel}</span>
          </Button>
        )}
      </div>
    </article>
  );
}
export const RecommenderCard = withSafeRender(RecommenderCardImpl, "RecommenderCard");

/** One thing a package leaves out; `hint` is a smaller second line, e.g. the add-on that brings it. */
export interface RecommenderMissingItem {
  label: string;
  hint?: string;
}

export interface RecommenderNotIncludedProps {
  /** What a package leaves out, one grey bar each. */
  items?: RecommenderMissingItem[];
  /** Read before each item by a screen reader. Defaults to "Not included". */
  cue?: string;
}

/** The picks a package leaves out, drawn under its card in the rail. */
function RecommenderNotIncludedImpl({ items, cue }: RecommenderNotIncludedProps) {
  const missing = list<RecommenderMissingItem>(items)
    .map(item => ({
      label: text(item?.label),
      hint: text(item?.hint)
    }))
    .filter(item => item.label !== "");
  if (missing.length === 0) {
    return null;
  }
  const missingCue = text(cue) || "Not included";
  return (
    <ul className={styles.notIncluded}>
      {missing.map(({ label, hint }, index) => (
        <li className={styles.notIncludedItem} key={`${index}-${label}`}>
          <svg aria-hidden="true" className={styles.notIncludedGlyph} focusable="false" viewBox={CLOSE_GLYPH_VIEWBOX}>
            <path d={CLOSE_GLYPH_PATH} fill="currentColor" />
          </svg>
          {/* Plain text: the label comes from data, and Text would paint its own tone over the chip. */}
          <span className={styles.notIncludedText}>
            <span className="dds-text dds-body-3 dds-weight-medium">
              <span className="dds-sr-only">{missingCue}: </span>
              {label}
            </span>
            {hint !== "" && (
              <span className="dds-text dds-label">
                <span className="dds-sr-only">, </span>
                {hint}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
export const RecommenderNotIncluded = withSafeRender(RecommenderNotIncludedImpl, "RecommenderNotIncluded");

export default RecommenderCard;
